import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDebouncedValue,
} from "@tellescope/ui";
import { formatAddress } from "@tellescope/utils";
import { ChevronsUpDown, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface LocationConfigRowProps {
  locationId: string;
  onLocationChange: (locationId: string) => void;
  onDelete: () => void;
}

export const LocationConfigRow = ({ locationId, onLocationChange, onDelete }: LocationConfigRowProps) => {
  const { t } = useTranslation("calendar");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search);
  const locationsQuery = useTellescopeInfiniteQuery({
    model: "appointment_locations",
    method: "getSome",
    request: {
      search: debouncedSearch
        ? {
            query: debouncedSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });
  const locations = useMemo(() => locationsQuery.data ?? [], []);
  const selectedLocationFromSearch = useMemo(
    () => locations.find(({ id }) => id === locationId),
    [locationId, locations]
  );

  const selectedLocationQuery = useTellescopeQuery({
    model: "appointment_locations",
    method: "getOne",
    request: locationId,
    enabled: !selectedLocationFromSearch && !!locationId,
  });

  const selectedLocation = useMemo(
    () => selectedLocationFromSearch ?? selectedLocationQuery.data,
    [selectedLocationFromSearch, selectedLocationQuery.data]
  );

  return (
    <div className="border rounded-md p-3 flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            role="combobox"
            aria-expanded={open}
            className="grow justify-between font-normal bg-muted text-muted-foreground text-xs"
          >
            <span className={selectedLocation ? undefined : "text-muted-foreground"}>
              {selectedLocation ? selectedLocation.title : t("Select location...")}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          onWheelCapture={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput placeholder={t("Search locations...")} value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty>{t("No locations found")}</CommandEmpty>
              <CommandGroup>
                {locationsQuery.data?.map((location) => (
                  <CommandItem
                    value={location.id}
                    key={location.id}
                    onSelect={() => {
                      onLocationChange(location.id);
                      setOpen(false);
                    }}
                  >
                    {location.title}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {!!selectedLocation && (
        <p className="text-muted-foreground">
          {formatAddress({
            ...selectedLocation,
            addressLineOne: selectedLocation.address ?? "",
            city: selectedLocation.city ?? "",
            state: selectedLocation.state ?? "",
            zipCode: selectedLocation.zipCode ?? "",
          })}
        </p>
      )}
      <Button
        variant="ghost"
        onClick={onDelete}
        size="sm-icon"
        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
      >
        <Trash2 />
      </Button>
    </div>
  );
};
